import { Change } from '@ritabygglov/core';
import { logger } from '@sentry/react';
import { isLocalStorageAvailable } from '../../utils/isLocalStorageAvailable';
import { IPendingChangeStore } from './IPendingChangeStore';

export class LocalStoragePendingChangeStore implements IPendingChangeStore {
  private readonly prefix: string;
  private readonly memory = new Map<string, Change[]>();
  private useLocalStorage: boolean;

  constructor(canvasId: string) {
    this.prefix = `pending_${canvasId}_`;
    this.useLocalStorage = isLocalStorageAvailable();
  }

  private key(batchId: string) {
    return `${this.prefix}${batchId}`;
  }

  private migrateToMemory() {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.prefix)) keys.push(key);
    }
    for (const key of keys) {
      try {
        const batchId = key.slice(this.prefix.length);
        const changes = JSON.parse(localStorage.getItem(key)!) as Change[];
        this.memory.set(batchId, changes);
      } catch {
        // ignore unparseable entries during migration
      }
      localStorage.removeItem(key);
    }
    this.useLocalStorage = false;
  }

  isUsingPersistentStorage(): boolean {
    return this.useLocalStorage;
  }

  save(batchId: string, changes: Change[]) {
    if (this.useLocalStorage) {
      try {
        localStorage.setItem(this.key(batchId), JSON.stringify(changes));
        return;
      } catch {
        logger.error(
          'localStorage write failed, migrating pending store to memory',
          { batchId }
        );
        this.migrateToMemory();
      }
    }
    this.memory.set(batchId, changes);
  }

  remove(batchId: string) {
    if (this.useLocalStorage) {
      localStorage.removeItem(this.key(batchId));
    } else {
      this.memory.delete(batchId);
    }
  }

  loadAll(): Map<string, Change[]> {
    if (!this.useLocalStorage) {
      return new Map(this.memory);
    }

    const result = new Map<string, Change[]>();
    const keys: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.prefix)) keys.push(key);
    }

    for (const key of keys) {
      const batchId = key.slice(this.prefix.length);
      try {
        const changes = JSON.parse(localStorage.getItem(key)!) as Change[];
        result.set(batchId, changes);
      } catch {
        logger.error('Failed to restore batch from localStorage', { batchId });
        localStorage.removeItem(key);
      }
    }

    return result;
  }
}
